import React, { useEffect, useState } from "react";
import Layout from "@components/layout";
import {
  currentLessonInfo,
  currentLevelInfo,
  getFromMemory,
  getItemWithHighestId,
  saveToMemory,
} from "@utils/storage.utils";
import showToast from "@utils/toast";
import { fetchLessonQuestionsAndAnswers } from "src/service/activities.services";
import { IActivityPage } from "src/types/index";

const QuestionsAndAnswers = () => {
  const [activities, setActivities] = useState<IActivityPage>({
    isLoading: true,
    activities: [],
  });

  const lessonId = getFromMemory("lessonId") as string;
  const levelId = getFromMemory("levelId") as string;
  const currentLanguage = getFromMemory("language") as string;

  const fetchActivities = async () => {
    if (!lessonId && !levelId) {
      return null;
    }

    try {
      const response = await fetchLessonQuestionsAndAnswers(
        lessonId,
        levelId,
        currentLanguage
      );

      if (response.status !== "success") {
        setActivities({ isLoading: false, activities: [] });
      } else {
        setActivities({ isLoading: false, activities: response.result });

        const activities = response.result;

        // Geting the item with the largest id from the array
        const highestActivity = getItemWithHighestId(activities);

        // Setting bases for increasing increamenting lesson id
        if (activities.length > 0) {
          saveToMemory("lastActivityId", highestActivity?.id);
          saveToMemory("activityIncr", "Yes");
        } else {
          saveToMemory(
            "lastActivityId",
            `ACT-${lessonId.split("LSN")[1].slice(1)}-01`
          );
          saveToMemory("activityIncr", "No");
        }
      }
    } catch (error: any) {
      setActivities({ isLoading: false, activities: [] });
      return showToast(`${error?.message}`, false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [lessonId, levelId, currentLanguage]);

  const { title, level } = currentLevelInfo();
  const currentLesson = currentLessonInfo();

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mt-6 mb-4">
          <h1 className="text-xl font-bold text-gray-900">
            Questions and Answers Activities
          </h1>
          <button
            onClick={() =>
              (window.location.href = `/dashboard/levels/lessons/activities/add`)
            }
            className="bg-brand text-white px-5 py-2 rounded-lg hover:bg-brand-dark transition duration-200"
          >
            {activities.isLoading
              ? "Activities Loading...."
              : "Add New Activity"}
          </button>
        </div>

        <p className="text-base text-black font-medium italic mb-5">
          Level {level} — {currentLesson.title} (Lesson{" "}
          {currentLesson.lessonNumber})
        </p>

        {activities.isLoading ? (
          <p>Loading Questions...</p>
        ) : activities.activities?.length === 0 ? (
          <p>No Questions available.</p>
        ) : activities.activities[0].activityType === "ChatActivity" ? (
          <p className="text-center mt-5 font-medium font-bold">
            No Questions and Answers Activities found
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 mt-3 gap-6">
              {activities.activities.map((activity, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-lg p-5 transition"
                >
                  <div className="flex justify-between">
                    <div className="">
                      <h2 className="text-lg font-bold text-brand">
                        {activity.activityType}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {activity.instruction}
                      </p>
                      <p className="text-sm text-gray-600">
                        {activity.localizedInstruction && (
                          <p className="italic text-sm text-gray-500">
                            {activity.localizedInstruction}
                          </p>
                        )}
                      </p>
                    </div>
                    <div className="">
                      <div className="rounded-full bg-brand w-8 h-8 flex items-center justify-center text-white font-bold">
                        {activity?.activityNumber ?? "—"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    {activity.question.audioUrl && (
                      <audio controls className="w-full">
                        <source
                          src={activity.question.audioUrl}
                          type="audio/mp3"
                        />
                      </audio>
                    )}
                    {activity.question.videoUrl && (
                      <video
                        className="w-full h-[18rem] mt-2 rounded-md"
                        controls
                      >
                        <source
                          src={activity.question.videoUrl}
                          type="video/mp4"
                        />
                      </video>
                    )}
                    {activity.question.text && (
                      <p className="text-gray-800 mt-2">
                        {activity.question.text}
                      </p>
                    )}

                    {activity.activityType === "FillTheGapActivity" && (
                      <div className="text-gray-800 mt-2 mb-2">
                        <p>{(activity.question as string) || "N/A"}</p>
                        {activity.audioUrl && (
                          <audio controls className="w-full">
                            <source
                              src={activity?.audioUrl || ""}
                              type="audio/mp3"
                            />
                          </audio>
                        )}
                      </div>
                    )}

                    {activity.activityType === "CrossWordActivity" &&
                      Array.isArray(activity.question) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                          <div>
                            {activity.question.map((qItem, index) => (
                              <div
                                key={index}
                                className="min-h-[80px] mb-2 p-4 border rounded bg-gray-50 flex items-center justify-center"
                              >
                                <p>{qItem.text}</p>
                              </div>
                            ))}
                          </div>

                          <div>
                            {activity.options.map((option) => (
                              <div
                                key={option.id}
                                className="min-h-[80px] mb-2 p-4 border rounded bg-gray-50 flex items-center justify-between"
                              >
                                <span>{option.text}</span>
                                <audio controls className="ml-4">
                                  <source
                                    src={option.audioUrl}
                                    type="audio/mp3"
                                  />
                                </audio>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>

                  {activity.activityType !== "CrossWordActivity" && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      {activity.options.map((option) => (
                        <div
                          key={option.id}
                          className="flex flex-col items-center p-2 bg-gray-100 rounded-md"
                        >
                          {option.imageUrl && (
                            <img
                              src={option.imageUrl}
                              alt={option.text}
                              className="w-16 h-16 rounded-md"
                            />
                          )}
                          {option.audioUrl && (
                            <audio controls className="mt-2">
                              <source src={option.audioUrl} type="audio/mp3" />
                            </audio>
                          )}
                          <p className="text-sm text-gray-700">{option.text}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex flex-row sm:flex-row gap-2 justify-between">
                    <div className="mt-4 bg-green-50 border border-green-300 rounded-md p-3 text-sm text-green-800">
                      <strong>Answer:</strong>{" "}
                      {Array.isArray(activity.answer)
                        ? activity.answer
                            .map((ans) =>
                              activity.options
                                .filter((opt) => opt.id === ans)
                                .map((opt) => opt.text)
                            )
                            .join(", ")
                        : activity.options.find(
                            (opt) => opt.id === activity.answer
                          )?.text || "N/A"}
                    </div>

                    <div>
                      <button
                        onClick={() =>
                          (window.location.href = `/dashboard/levels/lessons/activities/edit/${activity.activityType}/${activity.id}`)
                        }
                        className="block px-5 py-2 rounded-md border border-brand text-brand hover:bg-brand hover:text-white transition text-sm"
                      >
                        Edit Activity
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default QuestionsAndAnswers;
